"use client";

import { useCallback, useEffect } from "react";
import { METRICS_EVENTS } from "@cede/types";
import { ExchangeMissingTradeHistoryPermissionError, InvalidCredentialsError } from "@cede/utils";
import { RELINK_INFO, STEPS_INFO } from "./Content";
import { ApiKeysStep } from "./steps/ApiKeysStep";
import { CexSelectionStep } from "./steps/CexSelectionStep";
import { LinkStep } from "./steps/LinkStep";
import { LinkSocialsStep } from "@/components/features/wallet/ConnectAccount/steps/LinkSocialsStep";
import { PortalModal } from "@/components/ui/portal-modal";
import { useDevice, useHubUser } from "@/hooks";
import { useMetricsEvents, useTrackEvent } from "@/hooks/data/useMetricsEvents";
import { useOAuthSupportMap } from "@/hooks/exchangeAuthentication/useOAuthSupportMap";
import { useSocialAccounts } from "@/hooks/socials/useSocialAccounts";
import { cn } from "@/lib/utils";
import { useCreateAccountStore } from "@/store/accountStore";
import { STEPS, FLOW_TYPES, StepType, useConnectStore } from "@/store/connectStore";

const DESKTOP_STEP_DIMENSIONS: Record<
  StepType,
  {
    width: string;
    height: string;
  }
> = {
  [STEPS.ChooseCEX]: {
    width: "w-[640px]",
    height: "h-[80vh]",
  },
  [STEPS.LinkAccount]: {
    width: "w-[640px]",
    height: "h-[480px]",
  },
  [STEPS.ApiKeys]: {
    width: "w-[1100px]",
    height: "h-[85vh]",
  },
  [STEPS.LinkSocials]: {
    width: "w-[640px]",
    height: "h-[75vh]",
  },
} as const;

export const getStepText = (
  step: keyof typeof STEPS_INFO,
  textType: "title" | "subtitle",
  cex: string,
  isRelink?: boolean,
  isMobile?: boolean,
) => {
  const stepInfo = isRelink ? RELINK_INFO[step] : STEPS_INFO[step];
  const text = typeof stepInfo === "function" ? stepInfo(cex) : stepInfo;
  return isMobile ? text[textType].mobile : text[textType].desktop;
};

interface ConnectAccountProps {
  open: boolean;
  onClose: () => void;
}

export const ConnectAccount = ({ open, onClose }: ConnectAccountProps) => {
  const { isMobile } = useDevice();
  const { trackEvent } = useMetricsEvents();

  const { setOpenConnectModal, activeStep, setActiveStep, exchangeIds, flowType, socialAccountsRequired } =
    useConnectStore();
  const { formData, setError, reset } = useCreateAccountStore();
  const { hasRequiredSocialAccounts } = useSocialAccounts();

  const oAuthSupportMap = useOAuthSupportMap();

  const isRelink = flowType === FLOW_TYPES.RELINK;

  const hasLinkedSocials = hasRequiredSocialAccounts(socialAccountsRequired);

  const handleClose = useCallback(() => {
    // Track when user closes connect account modal
    trackEvent(METRICS_EVENTS.CONNECT_ACCOUNT_CLOSE, {});
    setOpenConnectModal(false);
    onClose();
  }, [trackEvent, setOpenConnectModal, onClose]);

  const { addExchangeAccount, addExchangeAccountIsLoading, authenticated } = useHubUser({
    onAddExchangeAccountSuccess: () => {
      handleNext();
      return;
    },
    onAddExchangeAccountError: (error) => {
      if (error instanceof InvalidCredentialsError || error instanceof ExchangeMissingTradeHistoryPermissionError) {
        setError(error);
      }
    },
    isAddExchangeAccountErrorShownInline: (error) => error instanceof ExchangeMissingTradeHistoryPermissionError,
  });

  const handleNext = (selectedCex?: string) => {
    let nextStep: StepType = activeStep;
    const isOAuthSupported = selectedCex && oAuthSupportMap[selectedCex];
    switch (activeStep) {
      case STEPS.ChooseCEX:
        nextStep = isOAuthSupported ? STEPS.LinkAccount : STEPS.ApiKeys;
        break;
      case STEPS.LinkAccount:
        nextStep = STEPS.ApiKeys;
        break;
      case STEPS.ApiKeys:
        if (flowType === FLOW_TYPES.ONBOARDING) handleClose();
        else {
          /**
           * @remarks
           * If the flow type is "standalone", we need to allow the user to connect other exchanges
           */
          if (flowType === FLOW_TYPES.STANDALONE) {
            nextStep = STEPS.ChooseCEX;
          } else if (!hasLinkedSocials) {
            nextStep = STEPS.LinkSocials;
          } else {
            handleClose();
          }
        }
        break;
      case STEPS.LinkSocials:
        handleClose();
        break;
    }

    // Track when user proceeds to next step in connect account flow
    trackEvent(METRICS_EVENTS.CONNECT_ACCOUNT_NEXT, {
      flowType,
      currentStep: activeStep,
      nextStep,
      cex: selectedCex || formData.selectedCex,
    });

    setActiveStep(nextStep);
  };

  const handleBack = async () => {
    let nextStep: StepType = activeStep;
    switch (activeStep) {
      case STEPS.ApiKeys:
        nextStep = oAuthSupportMap[formData.selectedCex] ? STEPS.LinkAccount : STEPS.ChooseCEX;
        break;
      case STEPS.LinkAccount:
        nextStep = STEPS.ChooseCEX;
        break;
      case STEPS.LinkSocials:
        nextStep = STEPS.ChooseCEX;
        break;
    }

    // Track when user goes back to previous step in connect account flow
    trackEvent(METRICS_EVENTS.CONNECT_ACCOUNT_BACK, {
      flowType,
      currentStep: activeStep,
      nextStep,
      cex: formData.selectedCex,
    });

    setActiveStep(nextStep);
  };

  // Only show modal if user is authenticated
  const isOpen = authenticated && open;

  // Track when connect account modal opens (fires only once when modal opens)
  useTrackEvent({
    event: METRICS_EVENTS.CONNECT_ACCOUNT_OPEN,
    properties: () => ({
      activeStep,
    }),
    enabled: isOpen,
  });

  const renderStep = () => {
    switch (activeStep) {
      case STEPS.ChooseCEX:
        return (
          <CexSelectionStep
            exchangeIds={exchangeIds}
            title={getStepText(STEPS.ChooseCEX, "title", "", isRelink, isMobile)}
            subtitle={getStepText(STEPS.ChooseCEX, "subtitle", "", isRelink, isMobile)}
            onNext={handleNext}
          />
        );
      case STEPS.LinkAccount:
        return (
          <LinkStep
            title={getStepText(STEPS.LinkAccount, "title", formData.selectedCex, isRelink, isMobile)}
            subtitle={getStepText(STEPS.LinkAccount, "subtitle", formData.selectedCex, isRelink, isMobile)}
            onNext={handleNext}
            onBack={handleBack}
            showBackButton={flowType !== FLOW_TYPES.ONBOARDING}
          />
        );
      case STEPS.ApiKeys:
        return (
          <ApiKeysStep
            title={getStepText(STEPS.ApiKeys, "title", formData.selectedCex, isRelink, isMobile)}
            subtitle={getStepText(STEPS.ApiKeys, "subtitle", formData.selectedCex, isRelink, isMobile)}
            onNext={addExchangeAccount}
            onBack={handleBack}
            isLoading={addExchangeAccountIsLoading}
            showBackButton={flowType !== FLOW_TYPES.ONBOARDING || !!oAuthSupportMap[formData.selectedCex]}
          />
        );
      case STEPS.LinkSocials:
        return (
          <LinkSocialsStep
            title={getStepText(STEPS.LinkSocials, "title", "", undefined, isMobile)}
            subtitle={getStepText(STEPS.LinkSocials, "subtitle", "", undefined, isMobile)}
            requiredSocialAccounts={socialAccountsRequired}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      default:
        return null;
    }
  };

  // Reset to initial step when modal closes
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setActiveStep(STEPS.ChooseCEX);
        reset();
      }, 300); // Wait for close animation
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen, setActiveStep, reset]);

  useEffect(() => {
    if (!authenticated && open) {
      handleClose();
    }
  }, [authenticated, open, handleClose]);

  const modalClassName = !isMobile
    ? cn(
        "max-w-[95vw] max-h-[90vh] z-modal-high",
        DESKTOP_STEP_DIMENSIONS[activeStep]?.width,
        DESKTOP_STEP_DIMENSIONS[activeStep]?.height,
      )
    : "z-modal-high";

  return (
    <PortalModal
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (nextOpen) setOpenConnectModal(true);
        else handleClose();
      }}
      className={modalClassName}
    >
      <PortalModal.Content key={activeStep}>{renderStep()}</PortalModal.Content>
    </PortalModal>
  );
};
